import { type Dispatch } from 'react';
import i18n from '../tools/i18n';
import type { IValidation, TCountry, IField } from '../types/outerLayout/common';
import { COUNTRIES, COUNTRIES_INFO } from '@app/dashboard/constants/countries';

export const isFormValid = (fields: object): boolean => {
  const valid = Object.values(fields).every(field => field.isValid);
  return valid;
};

export const validateField = (fields: IField, setFields: Dispatch<(prevState: IField) => IField>, fieldName: string, value: string) => {
  for (const validation of fields[fieldName].validations) {
    if (!validation.pattern.test(value)) {
      setFields(prevState => ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          isValid: false,
          errorMsg: validation.error_msg
        }
      }));
      return
    }
    setFields(prevState => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        isValid: true,
        errorMsg: ''
      }
    }));
  }
};

export const onFieldChangeHandler = (
  fields: IField,
  setFields: Dispatch<(prevState: IField) => IField>,
  fieldName: string,
  value: string,
  setServerErrMsg: Dispatch<string>,
) => {
  setServerErrMsg("");

  setFields(prevState => {
    return ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: value,
      }
    })
  });

  validateField(fields, setFields, fieldName, value);
};

export const required: IValidation = {
  pattern: /^.+$/,
  get error_msg() { return i18n.t('errors.required'); },
};

export const numbersOnly: IValidation = {
  pattern: /^\d+$/,
  get error_msg() { return i18n.t('errors.numbersOnly'); },
};

export const emailValidation: IValidation = {
  pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}$/,
  get error_msg() { return i18n.t('outerLayout.form.errors.invalidEmail'); },
};

export const urlValidation: IValidation = {
  pattern: /^(https?:\/\/)(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/[^\s]*)?$/,
  get error_msg() { return i18n.t('errors.invalidUrl'); },
};

export const passwordValidation = {
  atleastXCharacters: (numberOfCharacters: number) => ({
    pattern: new RegExp(`.{${numberOfCharacters},}$`),
    get error_msg() { return i18n.t('outerLayout.form.errors.atleastXCharacters').replace("{{number}}", numberOfCharacters.toString()); },
  }),
  atleastLowerCaseLetter: {
    pattern: /[a-z]/,
    get error_msg() { return i18n.t('outerLayout.form.errors.atleastLowerCaseLetter'); },
  },
  atleastUpperCaseLetter: {
    pattern: /[A-Z]/,
    get error_msg() { return i18n.t('outerLayout.form.errors.atleastUpperCaseLetter'); },
  },
  atleast1Number: {
    pattern: /\d/,
    get error_msg() { return i18n.t('outerLayout.form.errors.atleast1Number'); },
  },
  atleast1SpecialCharacter: {
    pattern: /[\W_]/,
    get error_msg() { return i18n.t('outerLayout.form.errors.atleast1SpecialCharacter'); },
  }
};

const MIN_CHARS_LENGTH = 8;

export const passwordValidations = [
  passwordValidation.atleastXCharacters(MIN_CHARS_LENGTH),
  passwordValidation.atleastUpperCaseLetter,
  passwordValidation.atleastLowerCaseLetter,
  passwordValidation.atleast1Number,
  passwordValidation.atleast1SpecialCharacter,
];

export const passwordValidationRules =
  passwordValidations.map(validationRule => ({
    pattern: validationRule.pattern,
    label: validationRule.error_msg
  }));

const equalityLiteral = "===equality_literal===";

export const passwordsComparisonLiteral = (password: string, confirmPassword: string) =>
  `${password}${equalityLiteral}${confirmPassword}`;

export const passwordMatchValidation = {
  pattern: new RegExp(`^(.+)${equalityLiteral}\\1$`),
  get error_msg() { return i18n.t('outerLayout.form.errors.matchPassword'); },
};

export const phoneNumberValidation = (country: TCountry): IValidation => {
  const minLength = COUNTRIES_INFO[country].phone_number_length.min;
  const maxLength = COUNTRIES_INFO[country].phone_number_length.max;

  return {
    pattern:
      country === COUNTRIES.EGY ?
        new RegExp(`^(010|011|012|015)\\d{${minLength - 3},${maxLength - 3}}$`)
        :
        new RegExp(`^\\d{${minLength},${maxLength}}$`)
    ,
    get error_msg() { return i18n.t('errors.invalidPhone'); }
  }
};

export const isPhoneNumber = (value: string) => {
  const MIN_PHONE_LENGTH = 6;

  const REGEXS = {
    IS_NUMERIC: /^\d+$/,
    FIRST_SIX_NUMERIC: /^\d{6,}/
  };

  const hasMinLength = value.length > 0;
  const hasMinNumericLength = value.length >= MIN_PHONE_LENGTH;

  const isNumeric = REGEXS.IS_NUMERIC.test(value);
  const isFirstSixNumeric = REGEXS.FIRST_SIX_NUMERIC.test(value);

  const hasValidPhoneNumber = (hasMinLength && isNumeric) || (hasMinNumericLength && !isNumeric && isFirstSixNumeric);
  return hasValidPhoneNumber;
};

export const maxXChars = (maxChars: number) => {
  return {
    pattern: new RegExp(`^.{0,${maxChars}}$`),
    get error_msg() {
      return i18n.t('errors.maxLength').replace('{{max}}', maxChars.toString());
    },
  };
};

// TS function overloading
export function optionalButHasValidation(validation: IValidation): IValidation;
export function optionalButHasValidation(validations: IValidation[]): IValidation[];
export function optionalButHasValidation(validation: IValidation | IValidation[]): IValidation | IValidation[] {

  const makeOptional = (val: IValidation): IValidation => {
    const stringifiedPattern = val.pattern.toString().slice(1, -1);
    const optionalPattern = stringifiedPattern.replace('^', '^(|').replace('$', ')$');
    return {
      pattern: new RegExp(optionalPattern),
      error_msg: val.error_msg,
    };
  };

  return Array.isArray(validation) ? validation.map(makeOptional) : makeOptional(validation);
};